
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Circle, User, Building, CreditCard } from 'lucide-react';
import LogoB from '../LogoB';
import { Separator } from '@/components/ui/separator';
import Logo from '../logo';
import Images from '@/assets/images';

export const ProfileCompletionProgress: React.FC = () => {
  const { isAddressCompleted, isBankDetailsCompleted ,isBankVerified} = useSelector((state: RootState) => state.profile);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const profileSteps = [
    {
      id: 'basic',
      title: 'Basic Information',
      description: 'Account setup completed',
      completed: isAuthenticated,
      icon: User,
    },
    {
      id: 'address',
      title: 'Address Details',
      description: 'Add your address information',
      completed: isAddressCompleted,
      icon: Building,
    },
    {
      id: 'bankdetails',
      title: 'Bank Details',
      completed: isBankDetailsCompleted,
      icon: Building,
    },
    // {
    //   id: 'venuedetails',
    //   title: 'Venue Details',
    //   completed: false,
    //   icon: Building,
    // },
    {
      id: 'payment',
      title: 'Payout Information',
      description: 'Verify bank details for payments',
      completed: isBankVerified,
      icon: CreditCard,
    },
  ];

  const completedSteps = profileSteps.filter(step => step.completed).length;
  const totalSteps = profileSteps.length;
  const completionPercentage = (completedSteps / totalSteps) * 100;

  return (
    <div className="w-full sm:w-80 sm:bg-flup-surface border-r border-flup-hairline sm:h-screen overflow-y-auto">
      <div className="p-4 m-auto hidden sm:flex flex-col">
        {/* <LogoB width='35' height='45' /> */}
        <img src={Images.Logo_BYZ} alt="CO-BYZ" className="h-16 w-16 rounded-lg object-contain" />
        {/* <p className='ml-10 text-lg font-bold'>Vendor Dashboard</p> */}
      </div>


      {/* Mobile device */}
      <div className="p-4 m-auto sm:hidden flex bg-flup-surface">
        {/* <LogoB height='30' width='30' /> */}
        <img src={Images.Logo_BYZ} alt="CO-BYZ" className="h-16 w-16 rounded-lg object-contain" />
      </div>


      <Separator />
      <div className="px-6 mt-2 bg-transparent">

        <div className="flex items-center  mb-2">
          {/* <div className="w-8 h-8 bg-flup-green  rounded-lg flex items-center justify-center">
            <span className="text-white text-sm font-bold">T</span>
          </div> */}
          <span className="hidden sm:flex text-xl font-bold text-flup-brand">Vendor Registration</span>
          <span className=" sm:hidden flex text-xl font-bold text-flup-ink">Vendor Registration</span>

        </div>

        <h2 className="text-xl font-semibold text-flup-ink mb-2 hidden sm:flex">Profile Setup</h2>
        <p className="text-flup-ink-2 mb-1 sm:mb-6 text-sm ">Complete your profile to start managing your venue</p>

        <div className="space-y-4 hidden sm:flex flex-col">
          {profileSteps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === 0 || profileSteps[index - 1]?.completed;

            return (
              <div key={step.id} className="flex items-start space-x-4">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.completed
                    ? 'bg-flup-brand text-white'
                    : isActive
                      ? 'bg-flup-green-soft border-2 border-flup-green text-flup-green-ink'
                      : 'bg-flup-surface-alt border-2 border-flup-hairline-2 text-flup-ink-3'
                    }`}>
                    {step.completed ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  {index < profileSteps.length - 1 && (
                    <div className={`w-0.5 h-12 mt-2 ${step.completed ? 'bg-flup-green ' : 'bg-flup-sunken'
                      }`} />
                  )}
                </div>

                <div className="flex-1">
                  <div className={`font-medium ${step.completed
                    ? 'text-flup-ink'
                    : isActive
                      ? 'text-flup-info'
                      : 'text-flup-ink-3'
                    }`}>
                    {step.title}
                  </div>
                  <div className={`text-sm mt-1 ${step.completed
                    ? 'text-green-600'
                    : isActive
                      ? 'text-flup-info'
                      : 'text-flup-ink-3'
                    }`}>
                    {step.completed ? 'Completed' : step.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="sm:mt-8 mt-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-flup-ink-2">Overall Progress</span>
            <span className="text-sm font-medium text-flup-ink-2">{Math.round(completionPercentage)}%</span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
          <div className="mt-2 text-xs text-flup-ink-3">
            {completedSteps} of {totalSteps} steps completed
          </div>
        </div>
      </div>
    </div>
  );
};
